import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, fmtCompact, fmtDate, fmtPct, currencySymbol } from "@/lib/format";
import { ArrowLeft, Download, Info } from "lucide-react";
import { toast } from "sonner";

const KPI_META = {
  revenue: { gradient: false },
  billed: { gradient: false },
  outstanding: { gradient: false },
  "balance-to-bill": { gradient: false },
  "contract-value": { gradient: false },
  "total-costs": { gradient: false },
  "gross-margin": { gradient: false },
  "net-margin": { gradient: false },
};

const STATUS_STYLES = {
  paid: "bg-[#047857] text-white",
  sent: "bg-[#0A0A0A] text-white",
  overdue: "bg-[#E53E3E] text-white",
  draft: "bg-[#F5F5F5] text-[#666] border border-[#E5E5E5]",
  partially_paid: "bg-[#D97706] text-white",
  void: "bg-[#999] text-white",
  open: "bg-[#0A0A0A] text-white",
  closed: "bg-[#047857] text-white",
};

const StatusPill = ({ value }) => {
  const v = (value || "").toLowerCase();
  const cls = STATUS_STYLES[v] || "bg-[#F5F5F5] text-[#666] border border-[#E5E5E5]";
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {v.replace(/_/g, " ") || "—"}
    </span>
  );
};

const ClassificationPill = ({ value }) => {
  const direct = value === "Direct";
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${direct ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] border border-[#E5E5E5] text-[#666]"}`}>
      {value}
    </span>
  );
};

const renderCell = (col, row) => {
  const v = row[col];
  if (v === null || v === undefined) return "—";
  if (col === "status") return <StatusPill value={v} />;
  if (col === "classification") return <ClassificationPill value={v} />;
  if (col === "entry_type") return v === "monthly" ? "Monthly" : "One-time";
  if (col === "date" || col === "due_date" || col === "period_or_date") return v || "—";
  if (col === "amount" || col === "balance" || col === "contract_value" || col === "billed") {
    return <span className="font-mono">{fmtMoney(v, row.currency || "USD", 2)}</span>;
  }
  if (col === "amount_usd" || col === "balance_usd") {
    return <span className="font-mono font-medium">{fmtMoney(v, "USD", 2)}</span>;
  }
  if (col === "days_overdue") {
    if (v === 0) return <span className="text-[#666]">—</span>;
    const tone = v > 90 ? "text-[#E53E3E]" : v > 30 ? "text-[#D97706]" : "text-[#666]";
    return <span className={`font-mono ${tone}`}>{v}d</span>;
  }
  if (typeof v === "number") return <span className="font-mono">{v}</span>;
  return String(v);
};

const colLabel = (c) => {
  const map = {
    invoice_number: "Invoice #",
    reference: "Reference",
    customer_name: "Customer",
    due_date: "Due",
    days_overdue: "Overdue",
    amount: "Amount",
    amount_usd: "USD",
    balance: "Balance",
    balance_usd: "Balance USD",
    contract_value: "Contract",
    period_or_date: "Period",
    entry_type: "Type",
    classification: "Class",
    mode: "Mode",
  };
  return map[c] || c.replace(/_/g, " ").replace(/\b\w/g, (s) => s.toUpperCase());
};

const isNumericCol = (c) => ["amount", "amount_usd", "balance", "balance_usd", "contract_value", "billed", "days_overdue"].includes(c);

const downloadCSV = (filename, columns, rows) => {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map(esc).join(",");
  const body = rows.map((r) => columns.map((c) => esc(r[c])).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const KpiDetail = () => {
  const { kpi } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setData(null);
      try {
        const r = await api.get(`/details/${kpi}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load detail");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [kpi]);

  const rows = data?.rows || [];
  const filtered = q ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) : rows;

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.2em] text-[#666] hover:text-[#0A0A0A]" data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Back to Dashboard
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#666] mb-2">
              Drill-down · {kpi.toUpperCase().replace(/-/g, " ")}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter" data-testid="detail-label">
              {data?.label || (loading ? "Loading…" : "—")}
            </h1>
            {data?.definition && (
              <div className="flex items-start gap-2 mt-3 text-sm text-[#666] max-w-3xl">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span data-testid="detail-definition">{data.definition}</span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <input
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="detail-search"
              className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none w-56"
            />
            {data?.columns && rows.length > 0 && (
              <button
                onClick={() => downloadCSV(`${data.kpi}.csv`, data.columns, filtered)}
                data-testid="detail-export-csv"
                className="border border-[#0A0A0A] px-3 py-2 text-sm font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Top summary card / components */}
      {data && (
        <section className="bg-white border border-[#E5E5E5] border-t-[3px] border-t-black p-6" data-testid="detail-summary">
          {data.components ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {Object.entries(data.components)
                .filter(([k]) => k !== "direct_cost_heads")
                .map(([k, v]) => {
                  const isPct = k.endsWith("_pct");
                  const display = isPct ? fmtPct(v) : fmtCompact(v, "USD");
                  return (
                    <div key={k}>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">
                        {k.replace(/_usd$/, "").replace(/_/g, " ")}
                      </div>
                      <div className="text-2xl font-mono font-medium tracking-tight">
                        {display}
                      </div>
                    </div>
                  );
                })}
              {data.components.direct_cost_heads && (
                <div className="md:col-span-5 pt-4 border-t border-[#E5E5E5]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">
                    Direct Cost Heads Used
                  </div>
                  <div className="text-xs font-mono text-[#0A0A0A]">
                    {data.components.direct_cost_heads.join(" · ")}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">
                  Total (USD)
                </div>
                <div className="text-5xl font-mono font-medium tracking-tight" data-testid="detail-total">
                  {fmtMoney(data.total_usd, "USD")}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">
                  Records
                </div>
                <div className="text-2xl font-mono">{data.row_count}</div>
              </div>
            </div>
          )}

          {data.aging_buckets && (
            <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">
                Aging Buckets
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(data.aging_buckets).map(([k, v]) => {
                  const tone = k === "90+" ? "border-l-[#E53E3E]" : k === "61-90" ? "border-l-[#D97706]" : k === "31-60" ? "border-l-[#999]" : "border-l-[#0A0A0A]";
                  return (
                    <div key={k} className={`bg-[#F5F5F5] border-l-[3px] ${tone} p-3`} data-testid={`aging-${k}`}>
                      <div className="text-[10px] font-mono uppercase text-[#666]">{k} days</div>
                      <div className="text-lg font-mono font-medium mt-1">{fmtMoney(v, "USD")}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.by_head && data.by_head.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">
                By Cost Head
              </div>
              <div className="space-y-2">
                {data.by_head.map((h) => {
                  const max = Math.max(...data.by_head.map((x) => x.amount_usd), 1);
                  const w = (h.amount_usd / max) * 100;
                  return (
                    <div key={h.head}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#333]">{h.head}</span>
                        <span className="font-mono">{fmtMoney(h.amount_usd, "USD")}</span>
                      </div>
                      <div className="w-full h-1.5 bg-[#F0F0F0]">
                        <div className="h-full bg-[#0A0A0A]" style={{ width: `${w}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.note && (
            <div className="mt-4 bg-[#F5F5F5] border-l-[3px] border-l-[#D97706] p-3 text-xs font-mono text-[#666]">
              {data.note}
            </div>
          )}
        </section>
      )}

      {/* Records table */}
      {data && data.columns && (
        <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="detail-table">
          <table className="w-full border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-[#F5F5F5] border-b border-[#0A0A0A]">
                {data.columns.map((c) => (
                  <th key={c} className={`text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4 ${isNumericCol(c) ? "text-right" : "text-left"}`}>
                    {colLabel(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={data.columns.length} className="text-center text-sm text-[#666] py-12">
                    {loading ? "Loading…" : "No records"}
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr key={r.id || idx} className="border-b border-[#E5E5E5] hover:bg-[#F9F9F9]" data-testid={`detail-row-${idx}`}>
                    {data.columns.map((c) => (
                      <td key={c} className={`py-3 px-4 text-sm ${isNumericCol(c) ? "text-right" : ""}`}>
                        {renderCell(c, r)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {loading && !data && <div className="text-center text-sm text-[#666] py-16">Loading…</div>}
    </div>
  );
};

export default KpiDetail;
